/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import axios from "axios";
import classes from "./payments.module.css";
import Skeleton from "../../../components/skeleton/skeleton";
import { format } from "date-fns";
import { PiCoinsFill } from "react-icons/pi";
import { FaReceipt, FaCalendarAlt } from "react-icons/fa";

const url = process.env.REACT_APP_BACKEND_URL;

export default function Payments() {
    const [payments, setPayments] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [totalCount, setTotalCount] = useState(0);
    const [totalRevenue, setTotalRevenue] = useState(0);
    const [mostRecentDate, setMostRecentDate] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasPreviousPage, setHasPreviousPage] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const fetchPayments = async (targetPage = 1) => {
        const config = {
            headers: {
                authorization: `Bearer ${localStorage.getItem("tok")}`,
            },
        };

        setIsLoading(true);

        try {
            const dateParams = `${startDate ? `&startDate=${startDate}` : ""}${endDate ? `&endDate=${endDate}` : ""}`;
            const searchParam = searchQuery ? `&searchQuery=${encodeURIComponent(searchQuery)}` : "";
            const response = await axios.get(
                `${url}/payment/all?page=${targetPage}&limit=10${searchParam}${dateParams}`,
                config
            );

            const data = response.data;
            setPayments(data.payments || []);
            setTotalCount(data.totalCount || 0);
            setTotalRevenue(data.totalRevenue || 0);
            setMostRecentDate(data.mostRecentDate || (data.payments?.[0]?.createdAt) || null);
            setPage(data.page || 1);
            setTotalPages(data.totalPages || 1);
            setHasNextPage(data.hasNextPage || false);
            setHasPreviousPage(data.hasPreviousPage || false);
        } catch (error) {
            console.error("Error fetching payments:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Debounce search input
    useEffect(() => {
        const handler = setTimeout(() => {
            setSearchQuery(searchTerm);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [searchTerm]);

    useEffect(() => {
        fetchPayments(1);
    }, [searchQuery, startDate, endDate]);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleClearDateFilter = () => {
        setStartDate("");
        setEndDate("");
    };

    const formatCurrency = (val) => {
        const num = parseFloat(val) || 0;
        return new Intl.NumberFormat("en-NG", {
            style: "currency",
            currency: "NGN",
            maximumFractionDigits: 0,
        }).format(num);
    };


    return (
        <div className={classes.container}>
            <div className={classes.subContainer}>
                {/* Stats Header */}
                <div className={classes.statsGrid}>
                    <div className={classes.statCard}>
                        <div className={classes.statIconContainer}>
                            <PiCoinsFill />
                        </div>
                        <div className={classes.statInfo}>
                            <span>Total Revenue</span>
                            <h3>{formatCurrency(totalRevenue)}</h3>
                        </div>
                    </div>

                    <div className={classes.statCard}>
                        <div className={classes.statIconContainer} style={{ background: "rgba(33, 150, 243, 0.1)", color: "#2196f3" }}>
                            <FaReceipt />
                        </div>
                        <div className={classes.statInfo}>
                            <span>Transactions</span>
                            <h3>{totalCount}</h3>
                        </div>
                    </div>

                    <div className={classes.statCard}>
                        <div className={classes.statIconContainer} style={{ background: "rgba(76, 175, 80, 0.1)", color: "#4caf50" }}>
                            <FaCalendarAlt />
                        </div>
                        <div className={classes.statInfo}>
                            <span>Most Recent Payment</span>
                            <h3 style={{ fontSize: "calc(16 / 1512 * 100vw)" }}>
                                {mostRecentDate ? format(new Date(mostRecentDate), "MMM dd, yyyy") : "N/A"}
                            </h3>
                        </div>
                    </div>
                </div>

                {/* Filters Row */}
                <div className={classes.filterRow}>
                    <div className={classes.inputContainer}>
                        <input
                            type="text"
                            placeholder="Search by transaction ID, payer name or email..."
                            value={searchTerm}
                            onChange={handleSearch}
                            className={classes.input}
                        />
                    </div>

                    <div className={classes.dateFilterContainer}>
                        <div className={classes.dateInputGroup}>
                            <label className={classes.dateLabel}>Start Date</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className={classes.dateInput}
                            />
                        </div>

                        <div className={classes.dateInputGroup}>
                            <label className={classes.dateLabel}>End Date</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className={classes.dateInput}
                            />
                        </div>

                        {(startDate || endDate) && (
                            <button
                                type="button"
                                onClick={handleClearDateFilter}
                                className={classes.clearDateBtn}
                            >
                                Clear Dates
                            </button>
                        )}
                    </div>
                </div>

                <p className={classes.count}>Showing {payments.length} of {totalCount} records</p>

                {isLoading ? (
                    <div className={classes.loaderContainer}>
                        <Skeleton width={"100%"} height={"100%"} />
                    </div>
                ) : payments.length === 0 ? (
                    <div className={classes.emptyState}>
                        <p>No payment records found.</p>
                    </div>
                ) : (
                    <>
                        <table className={classes.table}>
                            <thead>
                                <tr>
                                    <th>S/N</th>
                                    <th>Transaction ID</th>
                                    <th>Payer</th>
                                    <th>Amount</th>
                                    <th>Referred By</th>
                                    <th>Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {payments.map((payment, index) => (
                                    <tr key={payment._id}>
                                        <td>{(page - 1) * 10 + index + 1}</td>
                                        <td>
                                            <span className={classes.txId}>{payment.transactionId}</span>
                                        </td>
                                        <td>
                                            <div className={classes.userCell}>
                                                <span className={classes.userName}>
                                                    {payment.user
                                                        ? `${payment.user.first_name} ${payment.user.last_name}`
                                                        : "Unknown User"}
                                                </span>
                                                <span className={classes.userEmail}>
                                                    {payment.user?.email || payment.userId}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className={classes.amount}>
                                                {formatCurrency(payment.amount)}
                                            </span>
                                        </td>
                                        <td>
                                            {payment.referrer ? (
                                                <div className={classes.userCell}>
                                                    <span className={classes.userName}>
                                                        {`${payment.referrer.first_name} ${payment.referrer.last_name}`}
                                                    </span>
                                                    <span className={classes.userEmail}>
                                                        {payment.referrer.email}
                                                    </span>
                                                </div>
                                            ) : (
                                                <span className={classes.noReferrer}>Direct (None)</span>
                                            )}
                                        </td>
                                        <td>
                                            {payment.createdAt
                                                ? format(new Date(payment.createdAt), "MMM dd, yyyy HH:mm")
                                                : "N/A"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div className={classes.pagination}>
                            <button
                                onClick={() => fetchPayments(page - 1)}
                                disabled={!hasPreviousPage}
                            >
                                Previous
                            </button>
                            <span>
                                Page {page} of {totalPages}
                            </span>
                            <button
                                onClick={() => fetchPayments(page + 1)}
                                disabled={!hasNextPage}
                            >
                                Next
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}